import {
  Box,
  Button,
  Grid,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Backdrop,
  CircularProgress,
} from "@material-ui/core";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { MdModeEdit, MdOutlineClose } from "react-icons/md";
import axios from "axios";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import { Controller, useForm } from "react-hook-form";
import { useNotification } from "../../../../hooks/useNotification";
import useStyles from "../../../../themes/style.js";
import { getEmpName } from "../../../../utils/constant";
import { Autocomplete } from "@material-ui/lab";

const workModeOptions = [
  { mode: "On-site", description: "Employee works at the company's premises" },
  { mode: "Remote", description: "Employee works from a remote location" },
  { mode: "Hybrid", description: "Combination of on-site and remote work" },
  {
    mode: "Flexible",
    description: "Employee can choose their own work schedule and location",
  },
];

const gradeoptions = [
  { grade: "A1", description: "Exceptional Performance" },
  { grade: "A2", description: "Excellent Performance" },
  { grade: "B1", description: "Very Good Performance" },
  { grade: "B2", description: "Good Performance" },
  { grade: "C1", description: "Outstanding Performance" },
  { grade: "C2", description: "Above Average Performance" },
  { grade: "D1", description: "Average Performance" },
  { grade: "D2", description: "Below Average Performance" },
];

const OfficeDetails = ({ userDetails, onUpdate }) => {
  const classes = useStyles();
  const token = localStorage.getItem("token");
  const [loader, setLoader] = useState(false);
  const [enableEdit, setEnableEdit] = useState(false);
  const [userName, setUserName] = useState([]);
  const [employeeData, setEmployeeData] = useState({});
  const [departmentData, setDepartmentData] = useState([]);
  const [selectedDepartment, setSelectedDepartment] = useState("");

  const handleNotificationCall = useNotification();

  const validationSchema = Yup.object().shape({
    email: Yup.string()
      .required("Email is required")
      .email("Email must be a Valid Email Address"),
    empId: Yup.string().nullable(),
    joinningDate: Yup.string()
      .required("Date of joining is required")
      .nullable(),
    designationId: Yup.string().nullable(),
    departmentId: Yup.string().nullable(),
    companyBranchName: Yup.string().nullable(),
    DirectReportingTo: Yup.string().nullable(),
    DotReportingTo: Yup.string().nullable(),
    grade: Yup.string().nullable().required("This field is Required"),
    probationPeriod: Yup.number()
      .required("Mention Probation Period in month")
      .typeError("value must be a number")
      .transform((_, val) => (val !== "" ? Number(val) : null)),
    modeOfWork: Yup.string().nullable().required("This field is Required"),
  });

  const {
    register,
    control,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(validationSchema),
  });

  useEffect(() => {
    if (userDetails) {
      const employee = userDetails.employee
        ? userDetails.employee
        : userDetails.recruiter;

      const salaryData = userDetails.salary_detail;
      setValue("email", userDetails.email || "");
      setValue("designationId", userDetails.designation?.id || "");
      setValue("departmentId", userDetails.designation?.department.id || "");
      setValue("DirectReportingTo", userDetails.DirectReportingTo || "");
      setValue("DotReportingTo", userDetails.DotReportingTo || "");

      setValue("companyBranchName", employee?.companyBranchName || "");
      setValue("probationPeriod", salaryData?.probationPeriod || 0);
      setValue("grade", salaryData?.grade || "");
      setValue("modeOfWork", salaryData?.modeOfWork || "");
      setValue(
        "joinningDate",
        moment(employee?.joinningDate).format("YYYY-MM-DD"),
      );
      setValue(
        "empId",
        employee?.empId
          ? employee?.empId
          : employee?.employeeId
          ? employee?.employeeId
          : "",
      );
      setEmployeeData(employee);
      setSelectedDepartment(userDetails.designation?.department.id || "");
    }
    getAllUserList();
    fetchDeparmentsData();
  }, [userDetails]);

  const getDesignations = (departmentId) => {
    const department = departmentData.find((dept) => dept.id === departmentId);
    return department?.designations || [];
  };

  const designationDataOptions = getDesignations(selectedDepartment).map(
    (designation) => ({
      ...designation,
      group: designation.level.level, // Group by level name
    }),
  );

  const handleDepartmentChange = (value) => {
    setSelectedDepartment(value);
  };

  const getprobationPeriod = (period) => {
    const probationPeriod = Number(period);
    if (!probationPeriod) {
      return null;
    }

    if (probationPeriod <= 1) {
      return `${probationPeriod} Month`;
    }
    return `${probationPeriod} Months`;
  };

  const getAllUserList = async () => {
    try {
      setLoader(true);

      const response = await axios.get(
        `${process.env.REACT_APP_SERVER}auth/getAllUserList`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: token,
          },
        },
      );

      setLoader(false);
      if (response.data.status) {
        const responseData = response.data.data;
        const userList = responseData.filter((item) => item.fullName !== null);
        const userExcludingList = userList.filter(
          (item) => item.id !== userDetails.id,
        );
        setUserName(userExcludingList);
      } else {
        handleNotificationCall("error", response.data.message);
      }
    } catch (error) {
      setLoader(false);
      console.error(error);
    }
  };

  const fetchDeparmentsData = async () => {
    try {
      setLoader(true);
      axios({
        method: "get",
        url: `${process.env.REACT_APP_SERVER}department/viewAll`,
        headers: {
          "Content-Type": "application/json",
          Authorization: token,
        },
      }).then(function (response) {
        setLoader(false);
        if (response.data.status === true) {
          setDepartmentData(response.data.data);
        }
      });
    } catch (error) {
      console.log(error);
      setLoader(false);
    }
  };

  const handleEditEmployee = async (values) => {
    setLoader(true);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_SERVER}admin/editUserOfficeDetails`,
        {
          ...values,
          userId: userDetails.id,
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: token,
          },
        },
      );

      if (response.data.status) {
        handleNotificationCall("success", response.data.message);
        setEnableEdit(false);
        onUpdate();
      } else {
        handleNotificationCall("error", response.data.message);
      }
      setLoader(false);
    } catch (error) {
      setLoader(false);
      console.error(error);
    }
  };

  return (
    <div>
      <Box>
        <div className="emp-profile-details-table-card">
          <h3 className="gradienttext">Office Details</h3>

          {!enableEdit ? (
            <button
              className="emp-profile-edit-button"
              onClick={() => setEnableEdit(!enableEdit)}
            >
              <MdModeEdit color="#056dfa" className="svg-icon" />
              <span className="lable">Edit</span>
            </button>
          ) : (
            <button
              className="emp-profile-edit-button emp-profile-cancel-button"
              onClick={() => setEnableEdit(false)}
            >
              <MdOutlineClose color="#fa0505" className="svg-icon" />
              <span className="lable">Cancel</span>
            </button>
          )}
          {!enableEdit ? (
            <Grid container spacing={3}>
              <Grid item xs={12} md={6} lg={6}>
                <table className="emp-profile-details-table">
                  <tbody>
                    <tr>
                      <td>Official Email</td>
                      <td>{userDetails.email}</td>
                    </tr>
                    <tr>
                      <td>Designation</td>
                      <td>
                        {userDetails.designation ? (
                          userDetails.designation.title
                        ) : (
                          <span className="emp-details-unvailable">
                            Unavailable
                          </span>
                        )}
                      </td>
                    </tr>
                    <tr>
                      <td>Department</td>
                      <td>
                        {userDetails.designation ? (
                          userDetails.designation.department?.title
                        ) : (
                          <span className="emp-details-unvailable">
                            Unavailable
                          </span>
                        )}
                      </td>
                    </tr>
                    <tr>
                      <td>Date of Joining </td>
                      <td>
                        {(employeeData?.joinningDate &&
                          moment(employeeData?.joinningDate).format(
                            "DD-MM-YYYY",
                          )) || (
                          <span className="emp-details-unvailable">
                            Unavailable
                          </span>
                        )}
                      </td>
                    </tr>
                    <tr>
                      <td>Probation period</td>
                      <td>
                        {userDetails.salary_detail?.probationPeriod ? (
                          getprobationPeriod(
                            userDetails.salary_detail.probationPeriod,
                          )
                        ) : (
                          <span className="emp-details-unvailable">
                            Unavailable
                          </span>
                        )}
                      </td>
                    </tr>
                    <tr>
                      <td>Grade</td>
                      <td>
                        {userDetails.salary_detail?.grade || (
                          <span className="emp-details-unvailable">
                            Unavailable
                          </span>
                        )}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </Grid>
              <Grid item xs={12} md={6} lg={6}>
                <table className="emp-profile-details-table">
                  <tbody>
                    <tr>
                      <td>Direct Reporting To</td>
                      <td>
                        {userDetails.direct_reportingto ? (
                          getEmpName(userDetails.direct_reportingto)
                        ) : (
                          <span className="emp-details-unvailable">
                            Unavailable
                          </span>
                        )}
                      </td>
                    </tr>
                    <tr>
                      <td>Dotted Reporting To</td>
                      <td>
                        {userDetails.dot_reportingto ? (
                          getEmpName(userDetails.dot_reportingto)
                        ) : (
                          <span className="emp-details-unvailable">
                            Unavailable
                          </span>
                        )}
                      </td>
                    </tr>
                    <tr>
                      <td>Company Branch Name & Address</td>
                      <td>
                        {employeeData?.companyBranchName || (
                          <span className="emp-details-unvailable">
                            Unavailable
                          </span>
                        )}
                      </td>
                    </tr>

                    <tr>
                      <td>Employee Id</td>
                      <td>
                        {employeeData?.empId || (
                          <span className="emp-details-unvailable">
                            Unavailable
                          </span>
                        )}
                      </td>
                    </tr>
                    <tr>
                      <td>Mode Of Work</td>
                      <td>
                        {userDetails.salary_detail?.modeOfWork || (
                          <span className="emp-details-unvailable">
                            Unavailable
                          </span>
                        )}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </Grid>
            </Grid>
          ) : (
            <form
              onSubmit={handleSubmit(handleEditEmployee)}
              style={{ padding: "10px" }}
            >
              <Grid container direction="row" spacing={2}>
                <Grid item xs={12} sm={6} md={6} lg={6}>
                  <InputLabel shrink htmlFor="email">
                    Official Email
                  </InputLabel>
                  <FormControl className={classes.margin}>
                    <TextField
                      classes={{ root: classes.customTextField }}
                      InputProps={{ disableUnderline: true }}
                      size="small"
                      placeholder="Enter Email"
                      id="email"
                      name="email"
                      {...register("email")}
                      error={errors.email ? true : false}
                    />

                    <Typography variant="inherit" color="error">
                      {errors.email?.message}
                    </Typography>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={6} lg={6}>
                  <InputLabel shrink htmlFor="empId">
                    Employee ID
                  </InputLabel>
                  <FormControl className={classes.margin}>
                    <TextField
                      classes={{ root: classes.customTextField }}
                      InputProps={{ disableUnderline: true }}
                      size="small"
                      type="number"
                      placeholder="Enter Employee Id"
                      id="empId"
                      name="empId"
                      {...register("empId")}
                      error={errors.employeeId ? true : false}
                    />

                    <Typography variant="inherit" color="error">
                      {errors.empId?.message}
                    </Typography>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={6} lg={6}>
                  <InputLabel shrink htmlFor="companyBranchName">
                    Company Branch Name & Address
                  </InputLabel>
                  <FormControl className={classes.margin}>
                    <TextField
                      size="small"
                      classes={{ root: classes.customTextField }}
                      InputProps={{ disableUnderline: true }}
                      placeholder="Enter Company Branch Name & Address"
                      name="companyBranchName"
                      {...register("companyBranchName")}
                      error={errors.companyBranchName ? true : false}
                    />

                    <Typography variant="inherit" color="error">
                      {errors.companyBranchName?.message}
                    </Typography>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={6} lg={6}>
                  <InputLabel shrink htmlFor="probationPeriod">
                    Probation Period
                  </InputLabel>
                  <FormControl className={classes.margin}>
                    <TextField
                      size="small"
                      type="number"
                      classes={{ root: classes.customTextField }}
                      InputProps={{
                        disableUnderline: true,
                        inputProps: { min: 0 },
                      }}
                      placeholder="Enter Probation Period"
                      name="probationPeriod"
                      {...register("probationPeriod")}
                      error={errors.probationPeriod ? true : false}
                    />

                    <Typography variant="inherit" color="error">
                      {errors.probationPeriod?.message}
                    </Typography>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6} md={6} lg={6}>
                  <InputLabel shrink htmlFor="grade">
                    Grade
                  </InputLabel>
                  <FormControl className={classes.margin}>
                    <Controller
                      name="grade"
                      control={control}
                      defaultValue=""
                      render={({ field: { onChange, value } }) => (
                        <Select
                          value={value}
                          onChange={(e) => onChange(e.target.value)}
                          error={errors.grade ? true : false}
                          classes={{
                            root: classes.customTextField,
                            icon: classes.customSelectIcon,
                          }}
                          disableUnderline
                        >
                          <MenuItem value="">Select option</MenuItem>
                          {gradeoptions.map((group) => (
                            <MenuItem key={group.grade} value={group.grade}>
                              {group.grade}
                            </MenuItem>
                          ))}
                        </Select>
                      )}
                    />
                    <Typography variant="inherit" color="error">
                      {errors.grade?.message}
                    </Typography>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6} md={6} lg={6}>
                  <InputLabel shrink htmlFor="modeOfWork">
                    Mode of Work
                  </InputLabel>
                  <FormControl className={classes.margin}>
                    <Controller
                      name="modeOfWork"
                      control={control}
                      defaultValue=""
                      render={({ field: { onChange, value } }) => (
                        <Select
                          value={value}
                          onChange={(e) => onChange(e.target.value)}
                          error={errors.modeOfWork ? true : false}
                          classes={{
                            root: classes.customTextField,
                            icon: classes.customSelectIcon,
                          }}
                          disableUnderline
                        >
                          <MenuItem value="">Select option</MenuItem>
                          {workModeOptions.map((group) => (
                            <MenuItem key={group.mode} value={group.mode}>
                              {group.mode}
                            </MenuItem>
                          ))}
                        </Select>
                      )}
                    />
                    <Typography variant="inherit" color="error">
                      {errors.modeOfWork?.message}
                    </Typography>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <InputLabel shrink htmlFor="departmentId">
                    Department
                  </InputLabel>
                  <FormControl fullWidth>
                    <Controller
                      name="departmentId"
                      control={control}
                      render={({ field: { onChange, value } }) => (
                        <Select
                          value={value || ""}
                          onChange={(e) => {
                            onChange(e.target.value);
                            handleDepartmentChange(e.target.value);
                          }}
                          classes={{
                            root: classes.customSelectField,
                            icon: classes.customSelectIcon,
                          }}
                          error={errors.departmentId ? true : false}
                          displayEmpty
                          disableUnderline
                        >
                          <MenuItem value="" disabled>
                            Select Department
                          </MenuItem>
                          {departmentData.map((dept) => (
                            <MenuItem key={dept.id} value={dept.id}>
                              {dept.title}
                            </MenuItem>
                          ))}
                        </Select>
                      )}
                    />
                    <Typography variant="inherit" color="error">
                      {errors.departmentId?.message}
                    </Typography>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <InputLabel shrink htmlFor="designationId">
                    Designation
                  </InputLabel>
                  <FormControl fullWidth>
                    <Controller
                      name="designationId"
                      control={control}
                      render={({ field: { onChange, value } }) => (
                        <Autocomplete
                          classes={{
                            root: classes.designationSelect,
                          }}
                          options={designationDataOptions.sort((a, b) =>
                            a.group?.localeCompare(b?.group),
                          )} // Sort by group name
                          groupBy={(option) => option.group} // Group by the level name
                          getOptionLabel={(option) => option.title} // Display designation title
                          onChange={(event, value) =>
                            setValue("designationId", value ? value.id : null)
                          }
                          value={
                            getDesignations(selectedDepartment).find(
                              (user) => user.id === value,
                            ) || null // Match the value
                          }
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label=""
                              variant="filled"
                              placeholder="Select Designation"
                              className="requirement"
                            />
                          )}
                          renderGroup={(params) => (
                            <Box key={params.key}>
                              <Box className={classes.designationGroupTitle}>
                                <Typography
                                  variant="subtitle2"
                                  style={{
                                    fontWeight: "bold",
                                    color: "#3f51b5",
                                  }}
                                >
                                  {params.group}
                                </Typography>
                              </Box>
                              {params.children}
                            </Box>
                          )}
                        />
                      )}
                    />
                    <Typography variant="inherit" color="error">
                      {errors.designationId?.message}
                    </Typography>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={6} lg={6}>
                  <InputLabel shrink htmlFor="joinningDate">
                    Date of Joining
                  </InputLabel>
                  <FormControl className={classes.margin}>
                    <TextField
                      InputProps={{ disableUnderline: true }}
                      classes={{ root: classes.customTextField }}
                      size="small"
                      type="date"
                      placeholder="Select employee joining Date "
                      {...register("joinningDate")}
                      error={errors.joinningDate ? true : false}
                    />

                    <Typography variant="inherit" color="error">
                      {errors.joinningDate?.message}
                    </Typography>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={6} lg={6}>
                  <InputLabel shrink htmlFor="DirectReportingTo">
                    Direct Reporting To:
                  </InputLabel>
                  <Controller
                    name="DirectReportingTo"
                    control={control}
                    render={({ field }) => (
                      <Autocomplete
                        {...field}
                        className="filterFullWidth"
                        classes={{
                          root: classes.designationSelect,
                        }}
                        options={userName}
                        getOptionLabel={(option) => option.fullName}
                        onChange={(event, value) =>
                          setValue("DirectReportingTo", value ? value.id : null)
                        }
                        value={
                          userName.find((user) => user.id === field.value) ||
                          null // Match the value
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label=""
                            variant="filled"
                            placeholder="Select Direct Reporting To"
                            InputLabelProps={{ shrink: true }}
                            type="text"
                          />
                        )}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={6} lg={6}>
                  <InputLabel shrink htmlFor="DotReportingTo">
                    Dotted Reporting To:
                  </InputLabel>
                  <Controller
                    name="DotReportingTo"
                    control={control}
                    render={({ field }) => (
                      <Autocomplete
                        {...field}
                        className="filterFullWidth"
                        classes={{
                          root: classes.designationSelect,
                        }}
                        options={userName}
                        getOptionLabel={(option) => option.fullName}
                        onChange={(event, value) =>
                          setValue("DotReportingTo", value ? value.id : null)
                        }
                        value={
                          userName.find((user) => user.id === field.value) ||
                          null // Match the value
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label=""
                            variant="filled"
                            placeholder="Select Dotted Reporting To"
                            InputLabelProps={{ shrink: true }}
                            type="text"
                          />
                        )}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Button
                    variant="contained"
                    size="small"
                    color="secondary"
                    style={{ flex: 1, margin: "0px 6px" }}
                    onClick={() => setEnableEdit(false)}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    style={{ flex: 1, margin: "0px 6px" }}
                    type="submit"
                  >
                    Save
                  </Button>
                </Grid>
              </Grid>
            </form>
          )}
        </div>
        <Backdrop className={classes.backdrop} open={loader}>
          <CircularProgress color="inherit" />
        </Backdrop>
      </Box>
    </div>
  );
};

export default OfficeDetails;
